import { isPaletteId, type PaletteSelection } from './palettes';
import { normaliseHex } from './ramp';

const STORAGE_KEY = 'cp.palette';
const CUSTOM_PREFIX = 'custom:';

/**
 * Primary-colour preference persistence.
 *
 * `localStorage`, for the same reasons as the light/dark preference next door in
 * `themeStorage`: it is a display choice rather than a credential, losing it is
 * harmless, and it *should* survive closing the browser.
 *
 * Stored as a preset id (`"blue"`) or `"custom:#3B82F6"`. A plain string rather
 * than JSON — the value is one short token, and JSON.parse would just be another
 * thing to guard.
 *
 * Every access is guarded because Safari's private mode throws on setItem, and
 * storage can be disabled entirely by policy. Anything unrecognised reads back
 * as `null` so the caller falls through to the default palette; a stale or
 * hand-edited value must never be able to break the app's colours.
 */
export const paletteStorage = {
  get(): PaletteSelection | null {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === null) return null;

      if (stored.startsWith(CUSTOM_PREFIX)) {
        const hex = normaliseHex(stored.slice(CUSTOM_PREFIX.length));
        return hex ? { kind: 'custom', hex } : null;
      }

      return isPaletteId(stored) ? { kind: 'preset', id: stored } : null;
    } catch {
      return null;
    }
  },

  set(selection: PaletteSelection): void {
    const value = selection.kind === 'preset' ? selection.id : `${CUSTOM_PREFIX}${selection.hex}`;

    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Preference simply won't persist; not worth interrupting the user.
    }
  },
};
